import React, { useEffect, useState } from "react";
import { Backdrop, CircularProgress, Container } from "@mui/material";
import {
	useLocation,
	useNavigate,
	useParams
} from "react-router-dom";
import { cloneDeep } from "lodash";
import { ApiCallStatus } from "../../utils/constants";
import { fetchPromotionProductsData } from "../PageFunctions/productFunctions";
import { useArgusAuth } from "../../hooks/useArgusAuth";
import PromotionProductGrid from "../../components/PromotionProductGrid";
import { fetchProductStockInfo } from "../PageFunctions/priceFunctions";



const PromotionProductsPage = ({
	genericDataLoaded,
	genericPageContent
}) => {
	const {isAuthenticated, isLoading} = useArgusAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const {handle} = useParams();
	const [promotionProducts, setPromotionProducts] = useState(null);
	const [quantities, setQuantities] = useState(null);
	const [pricingData, setPricingData] = useState(null);

	const [total, setTotal] = useState(null);
	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [activeFilters, setActiveFilters] = useState({
		pageNumber: null
	});
	const [apiParametersReady, setApiParametersReady] = useState(false);

	const [pricesCallStatus, setPricesCallStatus] = useState(ApiCallStatus.NotStarted);
	const [promotionProductsCallStatus, setPromotionProductsCallStatus] = useState(ApiCallStatus.NotStarted);
	const [stockInfoCallStatus, setStockInfoCallStatus] = useState(ApiCallStatus.NotStarted);

	const fetchPromotionProductsDataAsync = async () => {
		await fetchPromotionProductsData({
			handle,
			pageNumber: activeFilters.pageNumber,
			setPromotionProducts,
			setTotal,
			setPageSize,
			setStatusInProgress: () => setPromotionProductsCallStatus(ApiCallStatus.InProgress),
			setStatusSuccess: () => setPromotionProductsCallStatus(ApiCallStatus.Succeeded),
			setStatusFailed: () => setPromotionProductsCallStatus(ApiCallStatus.Failed),
			setStatusError: () => setPromotionProductsCallStatus(ApiCallStatus.Error)
		});
	}

	const fetchProductStockInfoAsync = async () => {
		const productSkus = promotionProducts.map((x) => x.sku);
		await fetchProductStockInfo({
			productSkus,
            setPricingData,
			setQuantities,
			setStatusInProgress: () => setStockInfoCallStatus(ApiCallStatus.InProgress),
			setStatusSuccess: () => setStockInfoCallStatus(ApiCallStatus.Succeeded),
			setStatusFailed: () => setStockInfoCallStatus(ApiCallStatus.Failed),
			setStatusError: () => setStockInfoCallStatus(ApiCallStatus.Error)
		});
	};

	const handlePageNumberChange = async (event, value) => {
		await updateUrlQueryValue("pageNumber", Number(value));
	};

	const updateUrlQueryValue = async (key, value) => {
		setPromotionProductsCallStatus(ApiCallStatus.NotStarted);
		const searchParams = new URLSearchParams(location.search);

		searchParams.set(key, value);
		window.location.href = `${location.pathname}${!!searchParams.size > 0 ? "?" : ""}${searchParams.toString()}`;
	};

	const updateQueryParameters = async (queryParameters) => {
		window.location.href = `${location.pathname}${!!queryParameters ? "?" : ""}${queryParameters}`;
	}

	const scrollTop = () => {
		window.scrollTo(0, 0);
	};

	useEffect(() => {
		if (apiParametersReady === true) {
			fetchPromotionProductsDataAsync();
		}
	}, [apiParametersReady]);


	useEffect(() => {
		scrollTop();

		const searchParams = new URLSearchParams(location.search);

		const activeFiltersCopy = cloneDeep(activeFilters);

		const pn = searchParams.get("pageNumber");

		activeFiltersCopy.pageNumber = Number(pn) ?? 1;

		setPageNumber(Number(pn) ?? 1);
		setActiveFilters(activeFiltersCopy);
		setApiParametersReady(true);
	}, [location]);

	useEffect(() => {
		if (promotionProductsCallStatus === ApiCallStatus.Succeeded && !!promotionProducts && promotionProducts.length > 0) {
			fetchProductStockInfoAsync();
		}
	}, [promotionProductsCallStatus, promotionProducts]);

	const allLoadingStates = [
		promotionProductsCallStatus,
		stockInfoCallStatus,
		pricesCallStatus
	];

	const pageIsLoading = allLoadingStates.includes(ApiCallStatus.InProgress) || genericDataLoaded === false || isLoading === true;

	return (
		<Container maxWidth="lg">
			<PromotionProductGrid
				promotionProducts={promotionProducts}
				stockInfo={quantities}
				pricingData={pricingData}
				customerCategory={genericPageContent?.customerCategory}
				activeFilters={activeFilters}
				setActiveFilters={setActiveFilters}
				total={total}
				pageNumber={pageNumber}
				pageSize={pageSize}
				handlePageNumberChange={handlePageNumberChange}
				updateQueryParameters={updateQueryParameters}
			/>
			<Backdrop
				sx={{color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1}}
				open={pageIsLoading}
			>
				<CircularProgress color="inherit"/>
			</Backdrop>
		</Container>
	);
};
export default PromotionProductsPage;